import { useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "../redux/hooks";
import { toggleHorizontalScrolls } from "../redux/slices/generatorSlice";
/**
 * Custom hook to manage and persist horizontal scroll positions for specific sections in logbook.
 *
 * @param lbid - A unique identifier for the logbook.
 * @param sectionIndex - The index of the section whose scroll position is being managed.
 * @returns An object containing the current scroll position, a function to save the scroll position, and a function to read the saved scroll position.
 */
const useSavedHorizontalScroll = (lbid: string, sectionIndex: number) => {
  const { clearHorizontalScrolls } = useSelector(state => state.generatorSlice);
  const [scroll, setScroll] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const readSaved = () => {
    const read = localStorage.getItem("scrolls");
    if (read) {
      const parsed = JSON.parse(read);
      if (!parsed[lbid]) return;
      setScroll(parsed[lbid][String(sectionIndex)]);
    }
  };

  const saveScroll = (scroll: number) => {
    const read = localStorage.getItem("scrolls");
    const parsed = read ? JSON.parse(read) : {};

    if (!parsed[lbid]) parsed[lbid] = {};
    if (!parsed[lbid][String(sectionIndex)])
      parsed[lbid][String(sectionIndex)] = 0;
    parsed[lbid][String(sectionIndex)] = scroll;

    localStorage.setItem("scrolls", JSON.stringify(parsed));
    setScroll(parsed[lbid][String(sectionIndex)]);
  };

  const debouncedSaveScroll = (scroll: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      saveScroll(scroll);
    }, 300);
  };

  useEffect(() => {
    if (clearHorizontalScrolls) {
      saveScroll(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [clearHorizontalScrolls]);

  return { scroll, saveScroll: debouncedSaveScroll, readSaved };
};

export const useClearScrollState = () => {
  const dispatch = useDispatch();

  return () => {
    localStorage.removeItem("scrollY");
    localStorage.removeItem("scrolls");
    dispatch(toggleHorizontalScrolls());
    setTimeout(() => {
      dispatch(toggleHorizontalScrolls());
    }, 500);
  };
};

export default useSavedHorizontalScroll;
